import React, { useEffect, useRef, useState } from 'react';
import { BsChevronDown, BsChevronUp, BsFunnel } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip
} from '@chakra-ui/react';

import FilterLabel from '../../../../components/FilterLabel';
import FilterRadioGroup from '../../../../components/FilterRadioGroup';
import UserMultiSelectSearchDropdown from '../../../../components/UserMultiSelectSearchDropdown';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { selectCompanyDepartments } from '../../../../redux/modules/company';
import {
  clearListJobsFilters,
  selectListJobsFilters,
  setListJobsDepartmentMap,
  setListJobsFiltersAddProjectOwner,
  setListJobsFiltersCheckAllDepartments,
  setListJobsFiltersCheckDepartment,
  setListJobsFiltersProjectNameOrder,
  setListJobsFiltersRemoveProjectOwner
} from '../../../../redux/modules/jobs';
import { createMap } from '../../../../utils/arrayUtil';

const JobsFilter = () => {
  const dispatch = useDispatch();
  const initRef = useRef();
  const { departmentId } = useParams();
  const [departmentList, setDepartmentList] = useState([]);
  const { departments: filterDepartmentMap, projectNameOrder, projectOwners } = useSelector(selectListJobsFilters);
  const [checkAllIsChecked, setCheckAllIsChecked] = useState(
    Object.values(filterDepartmentMap).every(dept => dept.checked)
  );
  const companyDepartments = useSelector(selectCompanyDepartments);

  useEffect(() => {
    if (companyDepartments.length) {
      setDepartmentList(companyDepartments.slice(0, 4));

      if (!Object.values(filterDepartmentMap).length) {
        const checkboxArray = companyDepartments.map(department => ({
          departmentId: department.departmentId,
          departmentName: department.departmentName,
          checked: true
        }));
        const departmentMap = createMap(checkboxArray, 'departmentId');
        dispatch(setListJobsDepartmentMap(departmentMap));
      }
    }
    }, [companyDepartments, departmentId, dispatch]); // eslint-disable-line

  const handleShowMoreLessClick = () => {
    if (departmentList.length < 5) setDepartmentList(companyDepartments);
    if (departmentList.length >= 5) setDepartmentList(companyDepartments.slice(0, 4));
  };

  const handleClearFilters = () => {
    setCheckAllIsChecked(true);
    dispatch(clearListJobsFilters());
  };

  return (
    <Popover
      initalFocusRef={initRef}
      placement="left-start"
    >
      {({ isOpen, onClose }) => (
        <>
          <Tooltip label="Filter Projects">
            <Box>
              <PopoverTrigger>
                <IconButton
                  data-analytics-id={analyticsIds.projectsListPage.filterProjectsPopoverButton}
                  icon={<span><Icon as={BsFunnel} boxSize="24px" /></span>}
                  mr={4}
                  variant="accent"
                />
              </PopoverTrigger>
            </Box>
          </Tooltip>
          <Portal>
            <PopoverContent width="395px">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader px={6} py={4} textAlign="left">
                <Text color="greyScale.900" textStyle="bodyXxl500">
                  Filters
                </Text>
              </PopoverHeader>
              <PopoverBody pb={4} px={6} width="395px">
                <Box maxH="700px" overflow="auto" pb={3} pt={1} textAlign="left">
                  <Box my={2}>
                    <FilterLabel labelName="Project Name" />
                    <Box>
                      <FilterRadioGroup
                        value={projectNameOrder}
                        onChange={val => dispatch(setListJobsFiltersProjectNameOrder(val))}
                      />
                    </Box>
                  </Box>

                  <Divider />

                  <Box my={2} pt={1}>
                    <FilterLabel labelName="Departments" />
                    <Flex flexFlow="column nowrap">
                      <Checkbox
                        isChecked={checkAllIsChecked}
                        my={1}
                        spacing="1rem"
                        onChange={e => {
                          dispatch(setListJobsFiltersCheckAllDepartments(e.target.checked));
                          setCheckAllIsChecked(!checkAllIsChecked);
                        }}
                      >
                        <Box as="span" color="greyScale.900">
                          {`${checkAllIsChecked ? 'Uncheck' : 'Check'} All`}
                        </Box>
                      </Checkbox>

                      {departmentList.map(department => {
                        const { departmentId } = department;
                        return (
                          <Checkbox
                            isChecked={filterDepartmentMap[departmentId].checked}
                            key={departmentId}
                            my={1}
                            spacing="1rem"
                            onChange={e => {
                              dispatch(setListJobsFiltersCheckDepartment(departmentId));
                              if (e.target.checked) {
                                const checkAll = Object.values(filterDepartmentMap).every(dept => {
                                  if (dept.departmentId !== departmentId) {
                                    return dept.checked;
                                  }

                                  return !dept.checked;
                                });

                                setCheckAllIsChecked(checkAll);
                              } else if (!e.target.checked) {
                                setCheckAllIsChecked(false);
                              }
                            }}
                          >
                            <Box as="span" color="greyScale.900">
                              {department.departmentName}
                            </Box>
                          </Checkbox>
                        );
                      })}
                      {companyDepartments.length > 4 && (
                        <Box w="200px">
                          <Button
                            rightIcon={(
                              <Icon
                                as={departmentList.length < 5 ? BsChevronDown : BsChevronUp}
                              />
                            )}
                            variant="textPrimary"
                            onClick={handleShowMoreLessClick}
                          >
                            {`View ${departmentList.length < 5 ? 'More' : 'Less'}`}
                          </Button>
                        </Box>
                      )}
                    </Flex>
                  </Box>

                  <Divider />

                  <Box my={2}>
                    <FilterLabel labelName="Project Owner" toolTip="Filter projects by owner" />
                    <Box>
                      <UserMultiSelectSearchDropdown
                        userSelectOptions={projectOwners}
                        onUserRemove={user => dispatch(setListJobsFiltersRemoveProjectOwner(user))}
                        onUserSelect={user => dispatch(setListJobsFiltersAddProjectOwner(user))}
                      />
                    </Box>
                  </Box>

                  <Flex flexFlow="row nowrap" justifyContent="center" mt={6}>
                    <Button mx={1} variant="secondary" onClick={handleClearFilters}>
                      Reset Filters
                    </Button>
                    <Button mx={1} variant="primary" onClick={() => onClose()}>
                      Show Results
                    </Button>
                  </Flex>
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
};

export default JobsFilter;
